import { fromJS } from 'immutable'
import { intersection } from 'lodash-es'
import { useContext, useMemo, useState } from 'react'

import { ActionBar } from './ActionBar'
import { ColumnContext, contactAndMapsPlugins } from './MultiColumnBlock'
import { blockTypes } from '../blocks'
import { contentElements } from './ElementInsertionMenu'
import { emptyText } from '../../../editorSidebarBlockTemplates'
import { getPluginTypes } from './MultiColumnBlock.helpers'
import { useTranslate } from '../../../../utils/translate'
import Element from '../Element'

type ColumnProps = Readonly<{
  config: UnknownRecord
  data: ImmutableMap
  index: number
  column: ImmutableMap
  editorView: boolean
  editorMode?: 'view' | 'edit'
  requestModeChange?: (editorMode: 'view' | 'edit') => void
  parentEditorMode: 'view' | 'edit'
  parentRequestModeChange: (editorMode: 'view' | 'edit') => void
  onDataChange: (data: ImmutableMap) => void
  isNested: boolean
}>

export function Column({
  config,
  data,
  index,
  column,
  editorView,
  parentEditorMode,
  parentRequestModeChange,
  onDataChange,
  isNested,
}: ColumnProps) {
  const BlockComponent = blockTypes[column.get('type')]

  const columns = data.get('columns')
  const isMultiColumn = columns.size > 1

  // Support both the normal multi-column block data structure
  // (e.g. .type="multi-column", .data.columns[].type="epages.image"),
  // and the "plugin with text below" block data structure
  // (e.g. .type="image-with-text", .data.columns[].image.type="epages.image", .data.columns[].text.type="epages.text")
  const hasTextBelow = data.hasIn(['columns', '0', 'text'])
  const type = hasTextBelow
    ? data
        .getIn(['columns', '0'])
        .keySeq()
        .filterNot((k) => k === '_id' || k === 'text')
        .first()
    : column.get('type')

  const [addingColumnAtIndex] = useContext(ColumnContext)
  const isAddingColumn = addingColumnAtIndex > -1

  const hasReachedMaxColumns =
    columns.size > 3 ||
    fullWidthOnlyPlugins.includes(type) ||
    (columns.size >= 2 && intersection(getPluginTypes(columns), contactAndMapsPlugins).length > 0)

  const shouldShowAddColumnButtons = editorView && !isNested && !hasReachedMaxColumns

  const addColumn = (index: number, block: ImmutableMap, data) => {
    if (hasTextBelow) {
      block = fromJS({
        [block.get('type').replace('epages.', '')]: block,
        text: emptyText,
      })
    }
    onDataChange(data.update('columns', (columns: ImmutableList) => columns.insert(index, block)))
  }

  const blockOfSameType = useMemo(() => contentElements[type]?.generate(), [type])

  const [editorMode, setEditorMode] = useState<'view' | 'edit'>('view')
  const [editAction, setEditAction] = useState<'editImage' | null>(null)
  const handleEditButtonClick = (action: 'editImage' | null = null) => {
    setEditorMode('edit')
    setEditAction(action)
  }
  const handleRequestModeChange = (editorMode: 'view' | 'edit', editAction: null) => {
    setEditorMode(editorMode)
    setEditAction(editAction)
  }

  return (
    <div className={isNested ? undefined : 'dali-grid-column'}>
      {shouldShowAddColumnButtons &&
        (!isAddingColumn || index !== addingColumnAtIndex) &&
        (hasTextBelow ? (
          <PlusButton onClick={() => addColumn(index, blockOfSameType, data)} />
        ) : (
          <AddColumnButton index={index} onNewColumn={addColumn.bind(null, index)} />
        ))}
      {BlockComponent ? (
        <BlockComponent
          config={config}
          editorView={editorView}
          editorMode={editorMode}
          requestModeChange={handleRequestModeChange}
          data={column.get('data')}
          onDataChange={(block: Core.Block) => onDataChange(data.setIn(['columns', index, 'data'], block))}
        />
      ) : hasTextBelow ? (
        <>
          <Element
            config={config}
            editorView={editorView}
            editorMode={editorMode}
            editAction={editAction}
            requestModeChange={handleRequestModeChange}
            isMultiColumn={isMultiColumn}
            element={column.get(type)}
            onElementChange={(element: ImmutableMap) => onDataChange(data.setIn(['columns', index, type], element))}
          />
          <Element
            config={config}
            editorView={editorView}
            isMultiColumn={isMultiColumn}
            element={column.get('text')}
            onElementChange={(element: ImmutableMap) => onDataChange(data.setIn(['columns', index, 'text'], element))}
          />
        </>
      ) : (
        <Element
          config={config}
          editorView={editorView}
          editorMode={parentEditorMode || editorMode}
          editAction={editAction}
          requestModeChange={parentRequestModeChange || handleRequestModeChange}
          element={column}
          isMultiColumn={isMultiColumn}
          onElementChange={(element: ImmutableMap) => onDataChange(data.setIn(['columns', index], element))}
        />
      )}
      {editorView && !isNested && (
        <ActionBar
          index={index}
          data={data}
          element={hasTextBelow ? column.get(type) : column}
          onDataChange={onDataChange}
          onEditButtonClick={handleEditButtonClick}
        />
      )}
      {shouldShowAddColumnButtons &&
        index === columns.size - 1 &&
        (hasTextBelow ? (
          <PlusButton onClick={() => addColumn(index + 1, blockOfSameType, data)} />
        ) : (
          <AddColumnButton index={index + 1} onNewColumn={addColumn.bind(null, index + 1)} />
        ))}
    </div>
  )
}

function AddColumnButton({ index }: Readonly<{ index: number; onNewColumn: (b: ImmutableMap) => void }>) {
  const [addingColumnAtIndex, setAddingColumnAtIndex] = useContext(ColumnContext)
  if (index === addingColumnAtIndex) return null

  return <PlusButton onClick={() => setAddingColumnAtIndex(index)} />
}

function PlusButton({ onClick }: Readonly<{ onClick: () => void }>) {
  const t = useTranslate('interface', 'components.elementInsertionComponent')

  // defer required for seamless switching to another insertion menu when one is open
  const handleClick = () => setTimeout(onClick, 0)

  return (
    <button title={t('plusButton.label')} className="dali-grid-column-add-button" onClick={handleClick}>
      <span className="visually-hidden">{t('plusButton.label')}</span>
    </button>
  )
}

const fullWidthOnlyPlugins = [
  'ng.category',
  'epages.productslider',
  'epages.contentslider',
  'epages.imagegallery',
  'epages.divider',
  'epages.expander',
  'epages.html',
  'epages.logo',
]
